import { useCallback, useRef, useState } from 'react';
import { Arrow } from '@vfit/shared-icons';
import { ImageAtoms, ButtonSlide, CustomText, ContainerTopProduct } from '@vfit/shared/atoms';
import { useRouter } from 'next/router';
import { Fade } from 'react-awesome-reveal';
import { useDeviceType } from '@vfit/shared/hooks';
import { Text, VfModal } from '@vfit/shared/components';
import { IGradientImage, ITextModal } from '@vfit/shared/models';
import { fonts } from '@vfit/shared/themes';
import { requireStaticImageAtomComponent } from '@vfit/shared/data-access';
import { Player } from '@lottiefiles/react-lottie-player';
import { ITopHero } from './topHero.models';
import {
  DivImgContainer,
  SlideButtons,
  SlideProductButtonSlideContainer,
  SlideProductContainer,
  SlideProductContentBottom,
  SlideProductContentTop,
  SlideProductNavigation,
  SlideProductText,
  SlideProductContent,
  ModalTitle,
  ModalContent,
} from './topHero.style';

/**
 * @DEPRECATED
 * This component has been duplicated in the shared folder
 * lib/shared/components/src/lib/TopHeroDynamic
 */

const TopHero = ({
  backUrl,
  backLabel,
  title,
  image,
  imageMobile,
  action,
  actionClick,
  secondAction,
  secondActionClick,
  text,
  textAction,
  textActionClick,
  descriptionBottom,
  disclaimer,
  divider,
  paddingControl,
  lottieDescription,
  loadingDescription,
}: ITopHero) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [modal, setModal] = useState<ITextModal>({
    show: false,
  });
  const { isMobile } = useDeviceType();
  const { push, asPath } = useRouter();

  const handleBack = useCallback(() => {
    if (backUrl) {
      window.open(backUrl, '_blank');
    } else {
      push(asPath.slice(0, asPath.lastIndexOf('/')), undefined, { shallow: true });
    }
  }, [asPath, backUrl, push]);

  const scrollToContent = () => {
    if (textRef?.current) {
      textRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  // const handleClickInflation = (description: string | undefined, title?: string) => {
  //   if (description) {
  //     setModal({
  //       show: true,
  //       text: description || '',
  //       title: title || '',
  //       disableOrganizeText: true,
  //     });
  //   }
  // };

  const resetModal = () => {
    setModal({
      show: false,
      text: '',
      title: '',
      disableOrganizeText: false,
    });
  };

  return (
    <>
      <div>
        <SlideProductContainer>
          <SlideProductContent>
            <SlideProductContentTop>
              {backLabel && (
                <SlideProductNavigation>
                  <button type="button" className="back" onClick={handleBack}>
                    <div>
                      <Arrow rotate={130} />
                    </div>
                    <span>{backLabel}</span>
                  </button>
                </SlideProductNavigation>
              )}
              <Fade triggerOnce>
                <SlideProductText enablePaddingTop={!backLabel} paddingControl={paddingControl}>
                  {title && (
                    <div className="title">
                      <CustomText text={title} textAlign="left" />
                    </div>
                  )}
                  <div className="text" style={{ marginTop: 32, marginBottom: 32 }}>
                    {text && <CustomText textAlign="left" text={text} />}
                  </div>
                  <SlideButtons>
                    {action?.title && !isMobile && (
                      <SlideProductButtonSlideContainer>
                        <ButtonSlide
                          label={action.title}
                          onClick={() => actionClick?.()}
                          buttonColor="#e60000"
                          labelColor="#fff"
                          borderColor="#e60000"
                          hoverColor="#bd0000"
                          clickColor="#a10000"
                          name={`action_topHero_${action?.type?.toString() || ''}`}
                        />
                      </SlideProductButtonSlideContainer>
                    )}
                    {secondAction?.title && (
                      <SlideProductButtonSlideContainer>
                        <ButtonSlide
                          label={secondAction.title}
                          onClick={() => secondActionClick?.()}
                          buttonColor="#fff"
                          hoverColor="#262626"
                          hoverTextColor="#fff"
                          clickColor="#7e7e7e"
                          clickTextColor="#fff"
                          name={`action_topHero_second_${secondAction?.type?.toString() || ''}`}
                        />
                      </SlideProductButtonSlideContainer>
                    )}
                  </SlideButtons>
                </SlideProductText>
              </Fade>
            </SlideProductContentTop>
            {!isMobile && (
              <SlideProductContentBottom>
                <ButtonSlide
                  onClick={scrollToContent}
                  animated
                  small
                  smallSize={50}
                  rotation={90}
                  buttonColor="#fff"
                  hoverColor="rgba(255, 255, 255, 0.8)"
                  clickColor="rgba(255, 255, 255, 0.6)"
                  name="action_goToBottom"
                />
              </SlideProductContentBottom>
            )}
          </SlideProductContent>
          <DivImgContainer absolute>
            <ImageAtoms
              gradient={IGradientImage.GRADIENT_TOP_LEFT}
              nameCard="slideProduct"
              image={image}
              imageMobile={imageMobile}
              isMobile={isMobile}
            />
          </DivImgContainer>
        </SlideProductContainer>
      </div>

      <div
        style={{
          marginTop: !isMobile ? 80 : 0,
          marginBottom: !isMobile ? 80 : 0,
        }}
      >
        {lottieDescription && loadingDescription ? (
          <ContainerTopProduct>
            <Player
              src={requireStaticImageAtomComponent(lottieDescription).src}
              className="player"
              loop
              autoplay
            />
          </ContainerTopProduct>
        ) : (
          descriptionBottom && (
            <Text
              textConfig={{
                textAlign: 'left',
                text: descriptionBottom,
                size: 36,
                sizeMobile: 30,
                lineHeight: 45,
              }}
              textRef={textRef}
              divider={divider}
              disclaimer={disclaimer}
              actionText={textAction}
              actionClick={textActionClick}
            />
          )
        )}
      </div>

      <VfModal height={563} isOpen={modal.show} handleClose={resetModal} customFooter={undefined}>
        <>
          {modal.title && (
            <ModalTitle>
              <CustomText
                fontFamily={fonts.exbold}
                size={36}
                lineHeight={45}
                textAlign="left"
                text={modal.title}
              />
            </ModalTitle>
          )}
          {modal.text && (
            <ModalContent>
              <CustomText
                size={18}
                lineHeight={24}
                textAlign="left"
                text={modal.text}
                disableOrganizeText={modal.disableOrganizeText}
              />
            </ModalContent>
          )}
        </>
      </VfModal>
    </>
  );
};

export default TopHero;
